import {DataFetcher} from 'data-fetcher/data-fetcher';

const ONESECOND = 1000;
const PROD = 'prod';
const DEFAULT_DATASOURCE = 'defaults';

const DATA_UPDATE_RESPONSE = 'dataEngine_update_response';
const DATA_UPDATE_ERROR = 'dataEngine_update_error';
const DATA_UPDATE_REQUEST = 'dataEngine_update_request';
const DATA_UPDATE_HEARTBEAT = 'dataEngine_update_heartbeat';

const dynamicReducerPassthrough = (datasourceName) => (state = {}, action) => {
    if (action.datasource === datasourceName) {
        switch (action.type) {
        case DATA_UPDATE_RESPONSE:
            return action.data || state;
        case DATA_UPDATE_HEARTBEAT:
        default:
            return state;
        }
    }
    return state;
};

const dataEngineActionsReducer = (state = {}) => state;
const dataEngineReady = (state = false, action) => {
    switch (action.type) {
    case DATA_UPDATE_RESPONSE:
    case DATA_UPDATE_HEARTBEAT:
        return action.dataEngineReady || state;
    default:
        return state;
    }
};

class DataEngine {
    constructor (opts = {}) {
        this.opts = {
            env: PROD,
            poll: 10
        };

        const {
            datasources = {}
        } = opts;

        this.needInit = true;
        this.ready = false;
        this.active = false;
        this.intervalId = -1;
        this.lastCall = 0;

        this.updateOpts(opts);

        this.createDataFetcher();

        const datasourceManager = this.datasourceManager = this.dataFetcher.datasourceManager;
        datasourceManager.add(datasources);
        datasourceManager.setEnv(this.opts.env);

        this.createDatasourceBindings(datasources);
        this.mapStateToProps = this.dynamicStateToProps.bind(this);
        this.mapDispatchToProps = this.dynamicDispatch.bind(this);
        this.checkHeartbeatCallback = this.checkHeartbeat.bind(this);
        this.heartbeatUpdateCallback = this.heartbeatUpdate.bind(this);
        this.storeCallback = this.store.bind(this);
    }

    updateOpts (additionalOpts) {
        Object.assign(this.opts, additionalOpts);
        const {
            poll
        } = this.opts;

        const {
            env
        } = additionalOpts;

        const isPollable = poll !== false;
        if (env && this.datasourceManager) {
            this.datasourceManager.setEnv(env);
        }
        this.pollRate = isPollable && poll;
        this.isPollable = isPollable;
    }

    init (app, additionalOpts = {}) {
        this.app = app;
        if (this.needInit) {
            this.needInit = false;
            this.updateOpts(additionalOpts);
        }
        this.start();
    }

    fetch (dataSourceName, params, refresh = false) {
        return this.dataFetcher.get({
            dataSourceName,
            refresh,
            params
        });
    }

    fetchAndStore (datasourceName, params, refresh = false) {
        return this.fetch(datasourceName, params, refresh)
            .then(this.storeCallback, this.storeCallback);
    }

    createDataFetcher () {
        this.dataFetcher = new DataFetcher();
    }

    createDatasourceBindings (datasources) {
        const datasourcesToBind = {...datasources};
        // Get rid of the default datasource, since
        // it's only useful to the datasource manager
        delete datasourcesToBind[DEFAULT_DATASOURCE];
        const datasourceToProps = this.datasourceToProps = {};
        const datasourceReducers = this.datasourceReducers = {};
        Object.keys(datasourcesToBind).forEach((datasourceName) => {
            const datasource = datasourcesToBind[datasourceName];
            // Put the response into the store, unless told otherwise.
            if (datasource.store !== false) {
                const prop = datasource.prop || datasourceName;
                datasourceToProps[datasourceName] = prop;
                datasourceReducers[prop] = dynamicReducerPassthrough(datasourceName);
            }
        });
    }

    checkHeartbeat () {
        if (!this.active) {
            return true;
        }

        if (this.pollRate) {
            // Normalize poll interval to a truer value (minus request/response overhead)
            const lastCallMade = this.timecode() - this.lastCall;
            const wait = this.pollRate;
            const waitms = wait * ONESECOND;
            // Set a minimum floor on request polling to avoid
            // building up a backlog of outgoing requests when
            // connectivity is poor.
            const nextTick = lastCallMade >= waitms ? waitms : Math.max(waitms - lastCallMade, ONESECOND);

            this.tick(this.checkHeartbeatCallback, nextTick);

            if (lastCallMade > waitms) {
                this.executeHeartbeat();
                return true;
            }
        }

        return false;
    }

    start () {
        this.stop();
        this.active = true;
        return this.checkHeartbeat();
    }

    stop () {
        if (!this.active) {
            return;
        }
        this.active = false;

        // Clear interval and reset
        this.cancel();
        this.intervalId = -1;
    }

    cancel () {
        clearTimeout(this.intervalId);
    }

    tick (op, nextTick) {
        this.cancel();
        this.intervalId = setTimeout(op, nextTick);
    }

    timecode () {
        return Date.now();
    }

    executeHeartbeat () {
        const { primaryDatasource = this.opts.datasource, dataParams } = this.opts;
        this.lastCall = this.timecode();
        if (primaryDatasource) {
            const fetchDatasource = this.fetchAndStore(primaryDatasource, dataParams, true);
            if (this.isPollable) {
                fetchDatasource.then(this.heartbeatUpdateCallback, this.heartbeatUpdateCallback);
            }
        } else {
            this.heartbeatUpdate();
        }
    }

    dynamicStateToProps (state) {
        const {datasourceToProps} = this;
        return Object.assign({}, state, datasourceToProps);
    }

    heartbeatUpdate () {
        this.dispatch({
            type: DATA_UPDATE_HEARTBEAT,
            dataEngineReady: this.ready
        });
    }

    store ({ success, data, source, params, dataSourceName }) {
        const isOk = success === true || data !== null;
        const ready = this.ready === true || isOk;
        const datasourceToProps = this.datasourceToProps;
        this.ready = ready;
        const payload = {
            isFresh: source === 'server',
            source,
            type: isOk ? DATA_UPDATE_RESPONSE : DATA_UPDATE_ERROR,
            dataEngineReady: ready,
            prop: datasourceToProps[dataSourceName],
            datasource: dataSourceName,
            data,
            dataEngineActions: this.engineActions,
            params
        };
        this.dispatch(payload);
    }

    dynamicDispatch (dispatch, action) {
        this.dispatch = dispatch;
        return {
            dataEngineEvent: this.storeCallback,
            dataEngineHeartbeat: this.heartbeatUpdate
        };
    }

}

DataEngine.prototype.dataEngineReducers = Object.freeze({
    dataEngineActions: dataEngineActionsReducer,
    dataEngineReady
});

DataEngine.prototype.engineActions = Object.freeze({
    DATA_UPDATE_RESPONSE,
    DATA_UPDATE_ERROR,
    DATA_UPDATE_REQUEST,
    DATA_UPDATE_HEARTBEAT
});

export default DataEngine;
